import React from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { Button, colors, TableCell, TableRow } from '@insurancemenu/imenu-components-lib';
import capitalize from '@insurancemenu/imenu-components-lib/dist/helpers/capitalize';
import {
  AccountCircle,
  AddCircle,
  CheckCircle,
  Clock,
  DollarCircle,
  IconCheckmark,
  InfoButtonFilled,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';

import { useRootStore } from 'store/rootStore';
import { checkIfCuttingOff } from 'global/utils/checkIfCuttingOff';
import { EnrollmentPeriod, EnrollmentStage, Proposal, RfqProposalStatus } from 'types/enrollmentService';
import clsx from 'clsx';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { carrierLogoURL } from 'global/utils/carrierHelper';
import CarrierLogo from '@insurancemenu/imenu-components-lib/dist/components/productCard/common/CarrierLogo';
import MoreDropdown from '../MoreDropdown';
import { ConfirmButton, createStyles } from './styles';

interface ProposalTableRowPropsType {
  proposal: Proposal;
  enrollmentPeriod: EnrollmentPeriod;
  pdfDownloadUrl?: string;
}

const CarriersLogoCell: React.FC<{ proposal: Proposal }> = ({ proposal }) => {
  return [
    ...new Set(
      proposal.productLines
        .sort((a, b) => {
          return sortByProductLineId(a.id, b.id);
        })
        .flatMap((pLine) => pLine.carrierIds),
    ),
  ].map((carrier) => {
    return carrier && <CarrierLogo carrier={{ id: carrier, logo: carrierLogoURL(carrier), name: carrier }} />;
  });
};

const ProposalTableRow: React.FC<ProposalTableRowPropsType> = observer((params) => {
  const { proposal, enrollmentPeriod, pdfDownloadUrl } = params;
  const { domainStoreV2 } = useRootStore();
  const history = useHistory();

  const classes = createStyles();

  const { cuttingOffInFortnight, cutOffInDays } = checkIfCuttingOff(
    enrollmentPeriod.effectiveDate,
    domainStoreV2.domainHeader.properties.retroQuotingDaysAmount || 10,
  );

  const isRetroQuotingCutOff =
    cuttingOffInFortnight && cutOffInDays === 0 && proposal.status !== RfqProposalStatus.RELEASED;

  function getCuttingOffLabel(): JSX.Element | null {
    if (cuttingOffInFortnight && cutOffInDays === 0) {
      return <p className={classes.expiredLabel}>Cut off</p>;
    }
    if (cuttingOffInFortnight && cutOffInDays === 1) {
      return <p className={classes.expiredLabel}>Cuts off today</p>;
    }
    if (cuttingOffInFortnight && cutOffInDays > 1) {
      return <p className={classes.expiredLabel}>{cutOffInDays} days left</p>;
    }

    return null;
  }

  const StatusButton = (icon: JSX.Element, text: string, color: string): JSX.Element => (
    <Button
      color="custom"
      customColor={color}
      leftIconProp={icon}
      type="text"
      size="medium"
      className={classes.textProposalStatus}
    >
      {text}
    </Button>
  );

  const enrollingButton = StatusButton(<IconCheckmark />, 'Enrolling', colors.green[600]);
  const submittedButton = StatusButton(<IconCheckmark />, 'Submitted', colors.blue[600]);

  const onRedirect = (): void => {
    if (proposal.quotingStatus && proposal.quotingStatus !== RfqProposalStatus.RELEASED) {
      history.push(`/quote-checklist/${enrollmentPeriod.groupId}/${enrollmentPeriod.id}/${proposal.cartId}`);
    } else if (proposal.status && proposal?.status !== RfqProposalStatus.RELEASED) {
      history.push(
        getRFQProposalChecklistURL({
          periodId: enrollmentPeriod.id,
          cartId: proposal.cartId,
        }),
      );
    } else {
      history.push(`/proposal-v2/${enrollmentPeriod.id}?cartId=${proposal.cartId}&groupId=${enrollmentPeriod.groupId}`);
    }
  };

  const onProposalRedirect = isRetroQuotingCutOff ? undefined : onRedirect;

  const viewButton = (
    <Button size="medium" type="opaque" color="primary" width="16.8rem">
      View
    </Button>
  );

  const reviewAndConfirmButton = (
    <ConfirmButton size="medium" color="primary" width="16.8rem">
      Review & Confirm
    </ConfirmButton>
  );

  const generalStatusButtonBar = (): JSX.Element => {
    if (enrollmentPeriod.stage === EnrollmentStage.SUBMITTED && proposal.confirmed) {
      return submittedButton;
    }

    // eslint-disable-next-line no-nested-ternary
    return enrollmentPeriod.hasConfirmedProposal
      ? proposal.confirmed
        ? enrollingButton
        : viewButton
      : reviewAndConfirmButton;
  };

  const statusButtonBar = (): JSX.Element => {
    switch (proposal?.status) {
      case RfqProposalStatus.INITIATED:
        return StatusButton(<InfoButtonFilled />, 'Initiated', colors.green[600]);
      case RfqProposalStatus.PENDING_UW:
        return StatusButton(<Clock />, 'Pending UW', colors.orange[600]);
      case RfqProposalStatus.RATED:
        return StatusButton(<DollarCircle />, 'Rated', colors.yellow[600]);
      case RfqProposalStatus.PENDING_RELEASE:
        return StatusButton(<Clock />, 'Pending Release', colors.blue[600]);
      case RfqProposalStatus.ACTION_REQUIRED:
        return StatusButton(<AddCircle />, 'New Carrier Request', colors.orange[600]);
      case RfqProposalStatus.RESPONSE_PENDING:
        return StatusButton(<AccountCircle />, 'Pending Response', colors.purple[600]);
      case RfqProposalStatus.RESPONSE_READY:
        return StatusButton(<CheckCircle />, 'Response Ready', colors.blue[600]);
      case RfqProposalStatus.EMPLOYEES_ADDED:
        return StatusButton(<AccountCircle />, 'Browsing Ready', colors.green[600]);
      case RfqProposalStatus.RELEASED:
      default:
        return generalStatusButtonBar();
    }
  };

  return (
    <TableRow className={classes.tableRow}>
      <TableCell
        className={clsx(classes.tableCell, proposal?.preferred && classes.preferredProposal)}
        onClick={onProposalRedirect}
      >
        {proposal.name}
      </TableCell>
      <TableCell className={classes.tableCell}>
        {dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate)}
        {getCuttingOffLabel()}
      </TableCell>
      <TableCell className={classes.tableCell}>
        {proposal.productLines
          .sort((a, b) => {
            return sortByProductLineId(a.id, b.id);
          })
          .map((pLine) => capitalize(PRODUCT_LINES_CONTENT_MAP[pLine.id].label))
          .join(', ')}
      </TableCell>
      <TableCell className={classes.tableCell}>
        <CarriersLogoCell proposal={proposal} />
      </TableCell>
      <TableCell className={classes.tableCell} onClick={onProposalRedirect} width="8rem">
        {isRetroQuotingCutOff ? (
          <div className={classes.expiredStatusTag}>
            <InfoButtonFilled width="2.4rem" height="2.4rem" />
            Expired
          </div>
        ) : (
          statusButtonBar()
        )}
      </TableCell>
      <TableCell className={classes.tableCell}>
        <div className={classes.cellAlignWrapper}>
          <MoreDropdown
            enrollmentPeriod={enrollmentPeriod}
            cartId={proposal.cartId}
            proposal={proposal}
            redirectToProposalPage={onProposalRedirect}
            pdfDownloadUrl={pdfDownloadUrl}
          />
        </div>
      </TableCell>
    </TableRow>
  );
});

export default ProposalTableRow;
